'use client';

import { Globe } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LOCALE_FLAGS, LOCALE_NAMES, type Locale, SUPPORTED_LOCALES } from '@/lib/constants';
import { navigateToLocale } from '@/lib/utils/locale-navigation';

interface BlogLanguageSwitcherProps {
  currentLocale: Locale;
  translations?: Array<{ locale: Locale; exists: boolean }>;
}

export function BlogLanguageSwitcher({ currentLocale, translations }: BlogLanguageSwitcherProps) {
  const pathname = usePathname();
  const t = useTranslations('LanguageSwitcher');

  const handleLocaleChange = (newLocale: Locale) => {
    navigateToLocale(pathname, newLocale);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{LOCALE_NAMES[currentLocale]}</span>
          <span className="sm:hidden">{LOCALE_FLAGS[currentLocale]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(SUPPORTED_LOCALES as readonly Locale[]).map((locale) => {
          const translation = translations?.find((t) => t.locale === locale);
          const isAvailable = !translations || translation?.exists !== false;

          return (
            <DropdownMenuItem
              key={locale}
              onClick={() => handleLocaleChange(locale)}
              disabled={!isAvailable}
              className="flex items-center gap-2"
            >
              <span>{LOCALE_FLAGS[locale]}</span>
              <span>{LOCALE_NAMES[locale]}</span>
              {locale === currentLocale && <span className="ml-auto text-xs">✓</span>}
              {!isAvailable && (
                <span className="ml-auto text-xs text-muted-foreground">{t('notAvailable')}</span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
